import { ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';

export interface ButtonToggleOption {
  /** Value written to the bound form control when this segment is selected. */
  value: string;
  /** Translated, user-facing segment label. May be empty for an option that only has an icon. */
  label: string;
  /** Material icon name, shown before the label. */
  icon?: string;
  /** Accessible name. Needed when the option has an icon and no label. */
  ariaLabel?: string;
}

/**
 * Single-select segmented toggle: a row of mutually-exclusive options where the active one is
 * highlighted. Works as a form control (ControlValueAccessor); the value is the selected
 * option's `value`. Segments share the row width equally.
 */
@Component({
  selector: 'xemex-button-toggle',
  standalone: true,
  imports: [MatButtonToggleModule, MatIconModule],
  templateUrl: './button-toggle.component.html',
  styleUrl: './button-toggle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.button-toggle--compact]': 'compact()' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ButtonToggleComponent),
      multi: true,
    },
  ],
})
export class ButtonToggleComponent implements ControlValueAccessor {
  readonly options = input<ButtonToggleOption[]>([]);
  /** Sizes the toggle to its content, with smaller segments, instead of filling the row. */
  readonly compact = input<boolean>(false);

  readonly value = signal<string | null>(null);
  readonly disabled = signal(false);

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  onSelect(value: string): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  // --- ControlValueAccessor ---

  writeValue(value: string | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
